// MatrixView — Monatsplan-Ansicht: Mitarbeiter x Diensttage Matrix mit Zuweisungs-Dialog
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, ChevronDown, Users, Sun, Moon,
  AlertCircle, CheckCircle2, RefreshCw, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { dienstApi, dienstwunschApi, usersApi } from '../../lib/api';
import { cn } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { TooltipProvider } from '../ui/tooltip';
import {
  getWochentagDE,
  STATUS_CODES,
  QUALIFIKATION_KURZ,
  getFahrzeugFarbe,
  getKontrastFarbe,
} from './constants';
import MatrixCell from './MatrixCell';
import SollIstCounter from './SollIstCounter';
import AssignmentDialog from './AssignmentDialog';
import AuditLogModal from './AuditLogModal';
import DienstwunschModal from './DienstwunschModal';
import BulkAssignmentDialog from './BulkAssignmentDialog';
import { useAuthStore } from '../../lib/store';

export function MatrixView({ wacheId, abteilungId, abteilungName, canEdit }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [matrixData, setMatrixData] = useState(null);
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [fahrzeugtypen, setFahrzeugtypen] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState({ mitarbeiter: null, diensttag: null, data: null });
  const [besetztePositionen, setBesetztePositionen] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  // Wunsch-Modal State
  const [wunschModal, setWunschModal] = useState({ open: false, mitarbeiter: null, diensttag: null, wunsch: null });
  const [alleUsers, setAlleUsers] = useState([]);
  const [wuensche, setWuensche] = useState([]);  // Liste aller Wünsche im aktuellen Monat
  const currentUser = useAuthStore(state => state.user);
  // Multi-Select-State: nur EIN Mitarbeiter zur Zeit erlaubt.
  // selectedCells = Array von { mitarbeiter_id, datum, dienst_art }
  const [multiSelectMitarbeiterId, setMultiSelectMitarbeiterId] = useState(null);
  const [selectedCells, setSelectedCells] = useState([]);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  // Collapse-Status der Sonder-Blöcke (Default: eingeklappt für kompakte Ansicht)
  const [wlExpanded, setWlExpanded] = useState(false);
  const [prkExpanded, setPrkExpanded] = useState(false);
  const [gastExpanded, setGastExpanded] = useState(false);
  const monatStartRef = useRef(null);
  const heuteRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const initialScrollDoneRef = useRef(false);

  // Drag-and-drop Scroll (Maus + Touch) für die horizontale Matrix-Tabelle
  useEffect(() => {
    const viewport = scrollContainerRef.current;
    if (!viewport) return;

    let isDown = false;
    let startX = 0;
    let scrollLeftStart = 0;
    let moved = false;

    const onMouseDown = (e) => {
      if (e.target.closest('button, [role="button"], a, input, select, textarea, [data-prevent-drag]')) {
        return;
      }
      isDown = true;
      moved = false;
      startX = e.pageX - viewport.offsetLeft;
      scrollLeftStart = viewport.scrollLeft;
      viewport.style.cursor = 'grabbing';
      viewport.style.userSelect = 'none';
    };
    const onMouseLeave = () => {
      isDown = false;
      viewport.style.cursor = '';
      viewport.style.userSelect = '';
    };
    const onMouseUp = (e) => {
      if (moved) e.stopPropagation();
      isDown = false;
      viewport.style.cursor = '';
      viewport.style.userSelect = '';
    };
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - viewport.offsetLeft;
      const walk = x - startX;
      if (Math.abs(walk) > 5) moved = true;
      viewport.scrollLeft = scrollLeftStart - walk;
    };
    const onClickCapture = (e) => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        moved = false;
      }
    };

    viewport.style.cursor = 'grab';
    viewport.addEventListener('mousedown', onMouseDown);
    viewport.addEventListener('mouseleave', onMouseLeave);
    viewport.addEventListener('mouseup', onMouseUp);
    viewport.addEventListener('mousemove', onMouseMove);
    viewport.addEventListener('click', onClickCapture, true);

    return () => {
      viewport.removeEventListener('mousedown', onMouseDown);
      viewport.removeEventListener('mouseleave', onMouseLeave);
      viewport.removeEventListener('mouseup', onMouseUp);
      viewport.removeEventListener('mousemove', onMouseMove);
      viewport.removeEventListener('click', onClickCapture, true);
      viewport.style.cursor = '';
    };
  }, [matrixData]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Heutiges Datum in Berlin (Europe/Berlin) als YYYY-MM-DD String.
  // Browser-Zeitzone des Users ist egal — Dienstplanung ist immer Berliner Zeit.
  const todayBerlinISO = useMemo(() => {
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Europe/Berlin',
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  }, []);
  const todayBerlinYear = parseInt(todayBerlinISO.slice(0, 4), 10);

  // Index der Spalte in `matrixData.diensttage`, die als "heute" gilt:
  // → exakt heute, sonst die zeitlich nächst-gelegene Spalte (egal ob T oder N).
  const todayColIndex = useMemo(() => {
    if (!matrixData?.diensttage?.length) return -1;
    const todayTs = new Date(todayBerlinISO).getTime();
    let bestIdx = -1;
    let bestDiff = Infinity;
    matrixData.diensttage.forEach((dt, i) => {
      const diff = Math.abs(new Date(dt.datum).getTime() - todayTs);
      if (diff < bestDiff) { bestDiff = diff; bestIdx = i; }
    });
    return bestIdx;
  }, [matrixData, todayBerlinISO]);

  const { startDate, endDate } = useMemo(() => {
    // Kalenderjahr-Ansicht: 01.01. bis 31.12. des aktuellen Jahres
    return {
      startDate: `${year}-01-01`,
      endDate: `${year}-12-31`,
    };
  }, [year]);

  const loadData = useCallback(async () => {
    if (!abteilungId) return;
    setIsLoading(true);
    try {
      const [matrixRes, fzRes, typenRes, wuenscheRes] = await Promise.all([
        dienstApi.getMatrix({ von_datum: startDate, bis_datum: endDate, abteilung_id: abteilungId }),
        dienstApi.getFahrzeuge(),
        dienstApi.getFahrzeugtypen(),
        dienstwunschApi.list({ abteilung_id: abteilungId, von_datum: startDate, bis_datum: endDate }).catch(() => ({ data: [] })),
      ]);
      setMatrixData(matrixRes.data);
      setFahrzeuge(fzRes.data);
      setFahrzeugtypen(typenRes.data);
      setWuensche(wuenscheRes.data || []);
    } catch (error) {
      console.error('Load matrix error:', error);
      toast.error('Matrix konnte nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, abteilungId]);

  // Reload nur die Wünsche (nach Modal-Aktion)
  const reloadWuensche = useCallback(async () => {
    if (!abteilungId) return;
    try {
      const r = await dienstwunschApi.list({ abteilung_id: abteilungId, von_datum: startDate, bis_datum: endDate });
      setWuensche(r.data || []);
    } catch { /* silent */ }
  }, [abteilungId, startDate, endDate]);

  // Alle Wache-User laden für Aushilfe-Auswahl im Gast-Slot.
  // Wir nutzen den dedizierten Endpoint, damit auch Einteiler (ADMIN) und
  // Wachabteilungsleitung Mitarbeiter fremder Abteilungen sehen (Standard-/users
  // filtert für diese Rollen auf die eigene Abteilung).
  useEffect(() => {
    (async () => {
      try {
        const res = await usersApi.getAushilfeKandidaten();
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setAlleUsers(list);
      } catch (e) {
        console.error('Load aushilfe-kandidaten error:', e);
      }
    })();
  }, []);

  // Lookup: mitarbeiter_id|datum|dienst_art → Wunsch
  const wunschMap = useMemo(() => {
    const m = {};
    (wuensche || []).forEach(w => {
      m[`${w.mitarbeiter_id}|${w.datum}|${w.dienst_art}`] = w;
    });
    return m;
  }, [wuensche]);

  const getWunsch = useCallback((maId, datum, dienstArt) => {
    return wunschMap[`${maId}|${datum}|${dienstArt}`] || null;
  }, [wunschMap]);

  // Handler zum Öffnen des Wunsch-Modals.
  // - Für Mitarbeiter ohne Edit-Recht: nur eigene Zellen
  // - Für Edit-User: alle Zellen (Rechtsklick)
  const handleWunschOpen = useCallback((mitarbeiter, diensttag, wunsch) => {
    const isOwner = currentUser?.id === mitarbeiter?.id;
    if (!canEdit && !isOwner) return; // Mitarbeiter ohne Edit-Recht: nur eigene
    setWunschModal({ open: true, mitarbeiter, diensttag, wunsch });
  }, [currentUser?.id, canEdit]);

  // Scroll-zu-Heute: nutzt einfach `[data-heute-col="1"]` (= berechnete heute-Spalte
  // basierend auf Berlin-Datum, NICHT auf Dienst-Rhythmus). Funktioniert in jeder
  // Wachabteilung gleich, egal ob heute ein Dienst-Tag ist.
  const scrollToToday = useCallback((behavior = 'auto') => {
    if (todayBerlinYear !== year) return false;
    const viewport = scrollContainerRef.current;
    if (!viewport) return false;
    let attempts = 0;
    const tryScroll = () => {
      attempts += 1;
      const target = viewport.querySelector('[data-heute-col="1"]');
      if (target && target.offsetWidth > 0) {
        const centerOffset = target.offsetLeft - viewport.clientWidth / 2 + target.clientWidth / 2;
        viewport.scrollTo({ left: Math.max(0, centerOffset), behavior });
        return true;
      }
      if (attempts < 10) setTimeout(tryScroll, 50);
      return false;
    };
    requestAnimationFrame(tryScroll);
    return true;
  }, [year, todayBerlinYear]);

  useEffect(() => { loadData(); }, [loadData]);

  // Nach Daten-Load zur "heute"-Spalte zentrieren (Berlin-Zeit, IMMER vorhanden).
  useEffect(() => {
    if (isLoading || !matrixData) return;
    if (initialScrollDoneRef.current) return;
    if (todayBerlinYear !== year) {
      initialScrollDoneRef.current = true;
      return;
    }
    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts += 1;
      const viewport = scrollContainerRef.current;
      if (!viewport) {
        if (attempts > 60) clearInterval(intervalId);
        return;
      }
      const target = viewport.querySelector('[data-heute-col="1"]');
      if (target && target.offsetWidth > 0 && viewport.clientWidth > 0) {
        const centerOffset = target.offsetLeft - viewport.clientWidth / 2 + target.clientWidth / 2;
        viewport.scrollLeft = Math.max(0, centerOffset);
        initialScrollDoneRef.current = true;
        clearInterval(intervalId);
      } else if (attempts > 60) {
        clearInterval(intervalId);
      }
    }, 50);
    return () => clearInterval(intervalId);
  }, [isLoading, matrixData, year, todayBerlinYear]);

  // Reset Scroll-State bei Jahres- ODER Abteilungs-Wechsel
  useEffect(() => {
    initialScrollDoneRef.current = false;
  }, [year, abteilungId]);

  const getBesetztePositionen = (diensttag) => {
    if (!matrixData?.einteilungen || !matrixData?.mitarbeiter) return [];
    const key = `${diensttag.datum}_${diensttag.dienst_art}`;
    const positionen = [];

    const mitarbeiterLookup = {};
    matrixData.mitarbeiter.forEach(ma => {
      mitarbeiterLookup[ma.id] = `${ma.vorname} ${ma.nachname}`;
    });

    Object.entries(matrixData.einteilungen).forEach(([maId, maEinteilungen]) => {
      const einteilung = maEinteilungen[key];
      if (einteilung?.hauptfunktion) {
        positionen.push({
          funktion_kuerzel: einteilung.hauptfunktion.funktion_kuerzel,
          fahrzeug_id: einteilung.hauptfunktion.fahrzeug_id,
          mitarbeiter_id: maId,
          mitarbeiter_name: mitarbeiterLookup[maId] || 'Unbekannt',
        });
      }
    });

    return positionen;
  };

  const handleCellClick = (mitarbeiter, diensttag, data) => {
    setBesetztePositionen(getBesetztePositionen(diensttag));
    setSelectedCell({ mitarbeiter, diensttag, data, ist_praktikant: !!mitarbeiter?.ist_praktikant });
    setAssignDialogOpen(true);
  };

  // Multi-Select: Ctrl/Cmd/Shift+Klick auf Zelle togglet die Auswahl.
  // Zellen verschiedener Mitarbeiter dürfen NICHT gleichzeitig aktiv sein —
  // bei Wechsel wird die alte Auswahl verworfen.
  const handleMultiSelect = (mitarbeiter, diensttag, event) => {
    const key = { mitarbeiter_id: mitarbeiter.id, datum: diensttag.datum, dienst_art: diensttag.dienst_art };
    setSelectedCells((prev) => {
      // Mitarbeiter-Wechsel: alte Auswahl komplett verwerfen — für ALLE Modifier
      // (Ctrl, Shift, Meta). Sonst würde Shift+Klick versuchen einen Bereich auf
      // einem anderen Mitarbeiter zu bilden.
      if (multiSelectMitarbeiterId && multiSelectMitarbeiterId !== mitarbeiter.id) {
        setMultiSelectMitarbeiterId(mitarbeiter.id);
        return [key];
      }
      // Shift+Klick: Bereich zur letzten selektierten Zelle auf gleicher Zeile
      if (event?.shiftKey && prev.length > 0) {
        const last = prev[prev.length - 1];
        const dtIndex = (matrixData?.diensttage || []).findIndex(
          (d) => d.datum === diensttag.datum && d.dienst_art === diensttag.dienst_art
        );
        const lastIndex = (matrixData?.diensttage || []).findIndex(
          (d) => d.datum === last.datum && d.dienst_art === last.dienst_art
        );
        if (dtIndex >= 0 && lastIndex >= 0) {
          const [lo, hi] = dtIndex < lastIndex ? [dtIndex, lastIndex] : [lastIndex, dtIndex];
          const range = (matrixData.diensttage || []).slice(lo, hi + 1).map((d) => ({
            mitarbeiter_id: mitarbeiter.id, datum: d.datum, dienst_art: d.dienst_art,
          }));
          // Merge: bestehende Auswahl + neuer Bereich, ohne Duplikate
          const merged = [...prev];
          for (const r of range) {
            if (!merged.some((x) => x.datum === r.datum && x.dienst_art === r.dienst_art)) merged.push(r);
          }
          if (!multiSelectMitarbeiterId) setMultiSelectMitarbeiterId(mitarbeiter.id);
          return merged;
        }
      }
      // Ctrl/Cmd+Klick oder erster Klick: einzelne Zelle togglen
      const existingIdx = prev.findIndex((c) => c.datum === key.datum && c.dienst_art === key.dienst_art);
      if (existingIdx >= 0) {
        const next = prev.filter((_, i) => i !== existingIdx);
        if (next.length === 0) setMultiSelectMitarbeiterId(null);
        return next;
      }
      if (!multiSelectMitarbeiterId) setMultiSelectMitarbeiterId(mitarbeiter.id);
      return [...prev, key];
    });
  };

  const clearMultiSelection = () => {
    setSelectedCells([]);
    setMultiSelectMitarbeiterId(null);
  };

  const isCellSelected = (mitarbeiterId, datum, dienstArt) =>
    multiSelectMitarbeiterId === mitarbeiterId &&
    selectedCells.some((c) => c.datum === datum && c.dienst_art === dienstArt);

  const bulkMitarbeiter = useMemo(() => {
    if (!multiSelectMitarbeiterId || !matrixData) return null;
    return (matrixData.mitarbeiter || []).find((m) => m.id === multiSelectMitarbeiterId) || null;
  }, [multiSelectMitarbeiterId, matrixData]);

  // === Drag-to-Select via Maus =====================================
  // Refs statt State — vermeidet Re-Renders während des Ziehens.
  const dragAnchorRef = useRef(null);              // { mitarbeiter_id, datum, dienst_art }
  const isDragSelectingRef = useRef(false);        // aktuell aktives Ziehen
  const wasDragSelectingRef = useRef(false);       // gerade beendetes Ziehen → Klick unterdrücken

  const handleDragSelectStart = useCallback((mitarbeiter, diensttag) => {
    dragAnchorRef.current = {
      mitarbeiter_id: mitarbeiter.id,
      datum: diensttag.datum,
      dienst_art: diensttag.dienst_art,
    };
    isDragSelectingRef.current = false;
  }, []);

  const handleDragSelectEnter = useCallback((mitarbeiter, diensttag) => {
    const anchor = dragAnchorRef.current;
    if (!anchor) return;
    // Nur gleicher Mitarbeiter — Kreuz-Zeilen-Auswahl ist per Design nicht erlaubt
    if (anchor.mitarbeiter_id !== mitarbeiter.id) return;
    // Wenn wir noch auf derselben Zelle sind → nichts tun
    if (anchor.datum === diensttag.datum && anchor.dienst_art === diensttag.dienst_art && !isDragSelectingRef.current) {
      return;
    }
    const dtList = matrixData?.diensttage || [];
    const iA = dtList.findIndex((d) => d.datum === anchor.datum && d.dienst_art === anchor.dienst_art);
    const iC = dtList.findIndex((d) => d.datum === diensttag.datum && d.dienst_art === diensttag.dienst_art);
    if (iA < 0 || iC < 0) return;
    const [lo, hi] = iA < iC ? [iA, iC] : [iC, iA];
    const range = dtList.slice(lo, hi + 1).map((d) => ({
      mitarbeiter_id: mitarbeiter.id,
      datum: d.datum,
      dienst_art: d.dienst_art,
    }));
    isDragSelectingRef.current = true;
    setMultiSelectMitarbeiterId(mitarbeiter.id);
    setSelectedCells(range);
  }, [matrixData]);

  // Globaler mouseup: beendet die Drag-Select-Session. Ein zeitnah folgender
  // Klick auf die Ziel-Zelle darf den Zuweisungs-Dialog nicht mehr öffnen.
  useEffect(() => {
    const onUp = () => {
      if (isDragSelectingRef.current) {
        wasDragSelectingRef.current = true;
        // Kurz später wieder freigeben — reicht damit das synthetische click-Event
        // nach mouseup den Ref sieht und geschluckt wird.
        setTimeout(() => { wasDragSelectingRef.current = false; }, 200);
      }
      dragAnchorRef.current = null;
      isDragSelectingRef.current = false;
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, []);

  // Optimistisches lokales Update der Einteilung — keine Server-Round-Trip
  // → User sieht die Karte sofort. Soll/Ist-Counter wird im Hintergrund nachgezogen.
  const optimisticUpdate = (assignment) => {
    const fz = fahrzeuge.find(f => f.id === assignment.fahrzeug_id);
    const fn = fz?.funktionen?.find(f => f.kuerzel === assignment.funktion_kuerzel);
    const key = `${assignment.datum}_${assignment.dienst_art}`;

    if (assignment.type === 'clear') {
      setMatrixData(prev => {
        const newEinteil = { ...(prev.einteilungen || {}) };
        if (newEinteil[assignment.mitarbeiter_id]) {
          newEinteil[assignment.mitarbeiter_id] = { ...newEinteil[assignment.mitarbeiter_id] };
          delete newEinteil[assignment.mitarbeiter_id][key];
        }
        return { ...prev, einteilungen: newEinteil };
      });
      return;
    }

    let newEinteilung;
    if (assignment.type === 'status') {
      const statusFlags = {
        ist_krank: assignment.status_code === 'K',
        ist_urlaub: assignment.status_code === 'SU',
        ist_frei: assignment.status_code === '--' || assignment.status_code === 'F' || assignment.status_code === 'ZBV',
        ist_wandern: assignment.status_code === 'WA' || assignment.status_code === 'RW',
        ist_tausch: assignment.status_code === 'T',
        bemerkung: assignment.status_code === 'F'   ? 'Fortbildung'
                 : assignment.status_code === 'RW'  ? 'RTW Wandern'
                 : assignment.status_code === 'ZBV' ? 'ZBV'
                 : null,
      };
      newEinteilung = { ...statusFlags, hauptfunktion: null, sprungfunktionen: [] };
    } else {
      newEinteilung = {
        hauptfunktion: fz && {
          fahrzeug_id: assignment.fahrzeug_id,
          fahrzeug_name: fz.funkrufname,
          funktion_kuerzel: assignment.funktion_kuerzel,
          funktion_name: fn?.name || assignment.funktion_kuerzel,
        },
        sprungfunktionen: (assignment.springer_funktionen || []).map(sf => {
          const sfFz = fahrzeuge.find(f => f.id === sf.fahrzeug_id);
          const sfFn = sfFz?.funktionen?.find(f => f.kuerzel === sf.funktion_kuerzel);
          return {
            fahrzeug_id: sf.fahrzeug_id,
            fahrzeug_name: sfFz?.funkrufname,
            funktion_kuerzel: sf.funktion_kuerzel,
            funktion_name: sfFn?.name || sf.funktion_kuerzel,
          };
        }),
        ist_krank: false, ist_urlaub: false, ist_frei: false, ist_wandern: false, ist_tausch: false,
        ist_praktikant: !!assignment.ist_praktikant,
        aushilfe_user_id: assignment.aushilfe_user_id || null,
      };
    }

    setMatrixData(prev => ({
      ...prev,
      einteilungen: {
        ...(prev.einteilungen || {}),
        [assignment.mitarbeiter_id]: {
          ...(prev.einteilungen?.[assignment.mitarbeiter_id] || {}),
          [key]: newEinteilung,
        },
      },
    }));
  };

  // Stiller Hintergrund-Reload, der nur die Soll/Ist-Werte mitzieht.
  // Setzt den Scroll-State NICHT zurück.
  const backgroundReloadSollIst = async () => {
    try {
      const res = await dienstApi.getMatrix({ von_datum: startDate, bis_datum: endDate, abteilung_id: abteilungId });
      setMatrixData(prev => ({
        ...(prev || res.data),
        soll_ist: res.data.soll_ist,
        // Einteilungen vom Server übernehmen (sync auch von anderen Usern)
        einteilungen: res.data.einteilungen,
      }));
    } catch (_) { /* silent */ }
  };

  const handleCellCopy = async (source, target, isCopy) => {
    if (!isCopy) {
      toast.info('STRG (oder ⌘) halten zum Kopieren');
      return;
    }
    // Identische Zelle = Quelle == Ziel → kein Sinn
    if (
      source.mitarbeiter_id === target.mitarbeiter_id
      && source.datum === target.datum
      && source.dienst_art === target.dienst_art
    ) {
      toast.error('Quelle und Ziel sind identisch');
      return;
    }
    // Datum im deutschen Format TT.MM.YY anzeigen
    const formatDatum = (iso) => {
      try {
        const [y, m, d] = iso.split('-');
        return `${d}.${m}.${y.slice(-2)}`;
      } catch {
        return iso;
      }
    };
    const dienstLabel = target.dienst_art === 'N' ? 'Nacht' : 'Tag';
    // Backward-compatibility: alte Payloads ohne `kind` → 'funktion'
    const kind = source.kind || 'funktion';
    try {
      if (kind === 'status') {
        await dienstApi.setStatus({
          mitarbeiter_id: target.mitarbeiter_id,
          datum: target.datum,
          dienst_art: target.dienst_art,
          status_code: source.status_code,
        });
        toast.success(`${source.status_code} kopiert auf ${formatDatum(target.datum)} ${dienstLabel}`);
        optimisticUpdate({
          type: 'status',
          mitarbeiter_id: target.mitarbeiter_id,
          datum: target.datum,
          dienst_art: target.dienst_art,
          status_code: source.status_code,
        });
      } else {
        await dienstApi.setFunktionMitSpringer({
          mitarbeiter_id: target.mitarbeiter_id,
          datum: target.datum,
          dienst_art: target.dienst_art,
          fahrzeug_id: source.fahrzeug_id,
          funktion_kuerzel: source.funktion_kuerzel,
          springer_funktionen: source.springer_funktionen || [],
          ist_praktikant: !!source.ist_praktikant,
          abteilung_id: abteilungId,
        });
        toast.success(`${source.funktion_kuerzel} kopiert auf ${formatDatum(target.datum)} ${dienstLabel}`);
        optimisticUpdate({
          type: 'funktion_mit_springer',
          mitarbeiter_id: target.mitarbeiter_id,
          datum: target.datum,
          dienst_art: target.dienst_art,
          fahrzeug_id: source.fahrzeug_id,
          funktion_kuerzel: source.funktion_kuerzel,
          springer_funktionen: source.springer_funktionen || [],
          ist_praktikant: !!source.ist_praktikant,
        });
      }
      setTimeout(() => { backgroundReloadSollIst(); }, 300);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Kopieren fehlgeschlagen';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      toast.error(errorMsg);
    }
  };

  const handleAssign = async (assignment) => {
    try {
      if (assignment.type === 'funktion_mit_springer') {
        await dienstApi.setFunktionMitSpringer({
          mitarbeiter_id: assignment.mitarbeiter_id,
          datum: assignment.datum,
          dienst_art: assignment.dienst_art,
          fahrzeug_id: assignment.fahrzeug_id,
          funktion_kuerzel: assignment.funktion_kuerzel,
          springer_funktionen: assignment.springer_funktionen || [],
          ist_praktikant: !!assignment.ist_praktikant,
          aushilfe_user_id: assignment.aushilfe_user_id || null,
          // Context-Abteilung explizit mitschicken — wichtig für Gast-Slots
          // und für User ohne eigene abteilung_id (DEVELOPER/WACHLEITUNG).
          abteilung_id: abteilungId,
        });
      } else if (assignment.type === 'funktion') {
        await dienstApi.setFunktion({
          mitarbeiter_id: assignment.mitarbeiter_id,
          datum: assignment.datum,
          dienst_art: assignment.dienst_art,
          funktion_kuerzel: assignment.funktion_kuerzel,
          fahrzeug_id: assignment.fahrzeug_id,
          ist_hauptfunktion: true,
        });
        toast.success(`${assignment.funktion_kuerzel} zugewiesen`);
      } else if (assignment.type === 'status') {
        await dienstApi.setStatus({
          mitarbeiter_id: assignment.mitarbeiter_id,
          datum: assignment.datum,
          dienst_art: assignment.dienst_art,
          status_code: assignment.status_code,
        });
        toast.success(`Status ${assignment.status_code} gesetzt`);
      } else if (assignment.type === 'clear') {
        await dienstApi.clearEinteilung({
          mitarbeiter_id: assignment.mitarbeiter_id,
          datum: assignment.datum,
          dienst_art: assignment.dienst_art,
        });
        toast.success('Einteilung gelöscht');
      }
      // Sofort optimistisch updaten → kein Reload-Sprung
      optimisticUpdate(assignment);
      // Hintergrund-Sync für Soll/Ist (verzögert, damit Server konsistent ist)
      setTimeout(() => { backgroundReloadSollIst(); }, 300);
    } catch (error) {
      console.error('Assign error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Fehler bei der Zuweisung';
      if (typeof detail === 'string') errorMsg = detail;
      else if (Array.isArray(detail)) errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      toast.error(errorMsg);
      throw error;
    }
  };

  const goToPreviousYear = () => {
    initialScrollDoneRef.current = false;
    setCurrentDate(new Date(year - 1, 0, 1));
  };
  const goToNextYear = () => {
    initialScrollDoneRef.current = false;
    setCurrentDate(new Date(year + 1, 0, 1));
  };
  const goToToday = () => {
    if (todayBerlinYear !== year) {
      initialScrollDoneRef.current = false;
      setCurrentDate(new Date(todayBerlinYear, 0, 1));
    } else {
      scrollToToday('smooth');
    }
  };

  if (!abteilungId) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg font-medium">Keine Abteilung ausgewählt</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Navigation */}
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goToPreviousYear} title="Vorjahr">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={goToNextYear} title="Folgejahr">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">
                  Jahresplan {year}
                </h2>
                {abteilungName && (
                  <Badge variant="secondary" className="text-sm">
                    {abteilungName}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToToday}>Heute</Button>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditOpen(true)}
                    data-testid="btn-open-audit-log"
                    className="gap-1"
                  >
                    <History className="w-4 h-4" />
                    <span className="hidden sm:inline">Verlauf</span>
                  </Button>
                )}
                <Button variant="outline" size="icon" onClick={loadData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {canEdit && (
              <p className="mt-2 text-[11px] text-muted-foreground hidden md:block" data-testid="copy-hint">
                Tipp: Belegte Zelle mit <kbd className="px-1 py-0.5 border rounded bg-muted text-[10px] font-semibold">STRG</kbd> / <kbd className="px-1 py-0.5 border rounded bg-muted text-[10px] font-semibold">⌘</kbd> auf eine andere Zelle ziehen, um Funktion zu kopieren · Rechtsklick auf Zelle = Dienstwunsch eintragen / anzeigen
              </p>
            )}
          </CardContent>
        </Card>

        {/* Matrix */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-8"><Skeleton className="h-96" /></div>
            ) : !matrixData || matrixData.diensttage?.length === 0 ? (
              <div className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Keine Diensttage in diesem Monat</p>
              </div>
            ) : (
              <div
                ref={scrollContainerRef}
                className="w-full overflow-auto max-h-[calc(100vh-220px)] [--cell-w:2.5rem] sm:[--cell-w:3rem]"
              >
                <div className="w-max min-w-full">
                  {/* Monatsname-Zeile (sticky oben innerhalb Matrix-Scroll) */}
                  <div className="flex sticky top-0 z-30 h-8 bg-slate-100">
                    <div className="w-40 sm:w-72 lg:w-80 shrink-0 px-2 font-bold text-xs sm:text-sm bg-slate-100 border-r-2 border-b sticky left-0 top-0 z-50 flex items-center gap-2 h-8">
                      <Users className="w-3.5 h-3.5 text-primary" />
                      <span>Mitarbeiter</span>
                      <Badge variant="outline" className="ml-auto text-[10px]">
                        {matrixData.mitarbeiter?.length || 0}
                      </Badge>
                    </div>
                    {(() => {
                      // Monate gruppieren: pro Monat die Anzahl Diensttage berechnen
                      const monatsGruppen = [];
                      let currentGroup = null;
                      matrixData.diensttage.forEach(dt => {
                        const m = new Date(dt.datum).getMonth();
                        const y = new Date(dt.datum).getFullYear();
                        if (!currentGroup || currentGroup.monat !== m || currentGroup.jahr !== y) {
                          if (currentGroup) monatsGruppen.push(currentGroup);
                          currentGroup = {
                            monat: m, jahr: y, count: 1,
                            label: new Date(dt.datum).toLocaleDateString('de-DE', { month: 'long' }),
                          };
                        } else {
                          currentGroup.count += 1;
                        }
                      });
                      if (currentGroup) monatsGruppen.push(currentGroup);
                      return monatsGruppen.map((g, idx) => (
                        <div
                          key={`${g.jahr}-${g.monat}`}
                          className={cn(
                            'shrink-0 px-1 h-8 flex items-center justify-center text-xs sm:text-sm font-bold border-r-2 border-b border-slate-300',
                            idx % 2 === 0 ? 'bg-slate-100 text-slate-700' : 'bg-slate-200 text-slate-800'
                          )}
                          style={{ width: `calc(var(--cell-w) * ${g.count})` }}
                        >
                          <span className="hidden sm:inline">{g.label}</span>
                          <span className="sm:hidden">{g.label.substring(0, 3)}.</span>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Wochentag/Tag/Symbol-Zeile (sticky direkt unter Monatszeile) */}
                  <div className="flex sticky top-8 z-20 h-14 bg-slate-50">
                    <div className="w-40 sm:w-72 lg:w-80 shrink-0 px-2 py-1 font-semibold bg-slate-50 border-r-2 border-b-2 sticky left-0 z-40 h-14 flex items-center">
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Diensttage</span>
                    </div>

                    {matrixData.diensttage.map((dt, i) => {
                      const dtDate = new Date(dt.datum);
                      const dtMonat = dtDate.getMonth();
                      const istMonatsAnfang = dt.tag === 1 && dt.dienst_art === 'T';
                      // istHeute: exakt heute (Berlin) + Tagdienst → blau hervorgehoben.
                      // istHeuteSpalte: berechnete Scroll-Ziel-Spalte (jede Abteilung
                      // hat IMMER eine — nicht abhängig vom Dienst-Rhythmus).
                      const istHeute = dt.datum === todayBerlinISO && dt.dienst_art === 'T';
                      const istHeuteSpalte = i === todayColIndex;
                      // Monatsfarben alternierend, damit man die Monate optisch trennt
                      const monatsZebra = dtMonat % 2 === 0;
                      return (
                      <div
                        key={i}
                        ref={istHeute ? heuteRef : (istMonatsAnfang && dtMonat === month ? monatStartRef : null)}
                        data-month-start={istMonatsAnfang || undefined}
                        data-today={istHeute ? '1' : undefined}
                        data-heute-col={istHeuteSpalte ? '1' : undefined}
                        data-datum={dt.datum}
                        data-dienst-art={dt.dienst_art}
                        className={cn(
                          'w-10 sm:w-12 h-14 shrink-0 px-1 py-1 text-center border-r border-b-2 flex flex-col items-center justify-center',
                          dt.dienst_art === 'T'
                            ? (monatsZebra ? 'bg-amber-50 border-amber-200' : 'bg-amber-100 border-amber-200')
                            : (monatsZebra ? 'bg-indigo-50 border-indigo-200' : 'bg-indigo-100 border-indigo-200'),
                          istMonatsAnfang && 'border-l-2 border-l-slate-400',
                          istHeute && 'ring-2 ring-blue-500 ring-inset !bg-blue-100 !border-blue-400'
                        )}
                      >
                        <div className="text-[9px] sm:text-[10px] text-muted-foreground font-medium">
                          {getWochentagDE(dt.wochentag_kurz)}
                        </div>
                        <div className={cn('font-bold text-xs sm:text-sm', istHeute && 'text-blue-700')}>{dt.tag}.</div>
                        <div className="flex items-center justify-center">
                          {dt.dienst_art === 'T'
                            ? <Sun className={cn('w-3 h-3', istHeute ? 'text-blue-600' : 'text-amber-600')} />
                            : <Moon className={cn('w-3 h-3', istHeute ? 'text-blue-600' : 'text-indigo-600')} />}
                        </div>
                      </div>
                      );
                    })}
                  </div>

                  {/* Mitarbeiter-Zeilen (ohne Wachleitung, ohne Gast-Slots) */}
                  {matrixData.mitarbeiter.filter(ma => !ma.ist_wachleitung && !ma.ist_gast_slot).map((ma, rowIdx) => {
                    const istGastSlot = !!ma.ist_gast_slot;
                    return (
                    <div key={ma.id} className={cn(
                      'flex border-b matrix-row-lazy',
                      istGastSlot ? 'bg-violet-100 border-violet-200' :
                        (rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100')
                    )}>
                      <div className={cn(
                        'w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 z-10',
                        istGastSlot ? 'bg-violet-100' :
                          (rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100')
                      )}>
                        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                          {istGastSlot && (
                            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1 py-0 h-4 sm:h-5 shrink-0 font-semibold bg-violet-100 text-violet-800 border-violet-300">
                              Gast
                            </Badge>
                          )}
                          {!istGastSlot && ma.ist_uebergreifend && (
                            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1 py-0 h-4 sm:h-5 shrink-0 font-semibold bg-violet-50 text-violet-700 border-violet-300" title="Übergreifend">
                              ÜG
                            </Badge>
                          )}
                          {!istGastSlot && ma.dienstgrad && (
                            <Badge variant="outline" className="hidden sm:flex text-[10px] px-1.5 py-0.5 h-5 shrink-0 font-semibold min-w-[45px] justify-center">
                              {ma.dienstgrad}
                            </Badge>
                          )}
                          {!istGastSlot && ma.qualifikation && (
                            <Badge variant="secondary" className="hidden sm:flex text-[10px] px-1.5 py-0.5 h-5 shrink-0 bg-emerald-100 text-emerald-800 min-w-[55px] justify-center">
                              {QUALIFIKATION_KURZ[ma.qualifikation] || ma.qualifikation}
                            </Badge>
                          )}
                          <span className={cn(
                            'font-medium text-xs sm:text-sm truncate min-w-0 flex-1',
                            istGastSlot && 'text-violet-900 italic'
                          )}>
                            {istGastSlot ? (
                              `Gast ${ma.nachname}`
                            ) : (
                              <>
                                <span className="hidden sm:inline">{ma.nachname}, {ma.vorname}</span>
                                <span className="sm:hidden">{ma.nachname}</span>
                              </>
                            )}
                          </span>
                        </div>
                      </div>

                      {matrixData.diensttage.map((dt, colIdx) => {
                        const key = `${dt.datum}_${dt.dienst_art}`;
                        const einteilung = matrixData.einteilungen?.[ma.id]?.[key];

                        return (
                          <div key={colIdx} className="w-10 sm:w-12 shrink-0">
                            <MatrixCell
                              mitarbeiter={ma}
                              diensttag={dt}
                              einteilung={einteilung}
                              onCellClick={handleCellClick}
                              canEdit={canEdit}
                              fahrzeugTypNummern={matrixData.fahrzeug_typ_nummern}
                              fahrzeuge={fahrzeuge}
                              onCellCopy={handleCellCopy}
                              wunsch={getWunsch(ma.id, dt.datum, dt.dienst_art)}
                              onWunschOpen={handleWunschOpen}
                              canSeeWunschHover={true}
                              currentUserId={currentUser?.id}
                              isSelected={isCellSelected(ma.id, dt.datum, dt.dienst_art)}
                              onMultiSelect={handleMultiSelect}
                              onDragSelectStart={handleDragSelectStart}
                              onDragSelectEnter={handleDragSelectEnter}
                              wasDragSelectingRef={wasDragSelectingRef}
                            />
                          </div>
                        );
                      })}
                    </div>
                    );
                  })}

                  {/* Wachleitung-Zeilen (eigener Block zwischen Stamm-MA und Praktikanten/Gästen) */}
                  {(() => {
                    const wachleitungMA = matrixData.mitarbeiter.filter(ma => ma.ist_wachleitung && !ma.ist_gast_slot);
                    if (wachleitungMA.length === 0) return null;
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setWlExpanded(v => !v)}
                          className="flex w-full border-t-2 border-sky-300 bg-sky-100 hover:bg-sky-200/80 transition-colors"
                          data-testid="toggle-wachleitung"
                        >
                          <div className="w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 bg-sky-100 z-10 font-semibold text-xs sm:text-sm text-sky-900">
                            <div className="flex items-center gap-2">
                              {wlExpanded ? <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-sky-700" /> : <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-sky-700" />}
                              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-sky-700" />
                              <span>Wachleitung ({wachleitungMA.length})</span>
                            </div>
                          </div>
                          {matrixData.diensttage.map((_, i) => (
                            <div key={i} className="w-10 sm:w-12 shrink-0 border-r border-sky-200" />
                          ))}
                        </button>
                        {wlExpanded && wachleitungMA.map((ma, wlIdx) => (
                          <div key={ma.id} className={cn('flex border-b border-sky-100', wlIdx % 2 === 0 ? 'bg-sky-50' : 'bg-sky-100/60')}>
                            <div className={cn(
                              'w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 z-10',
                              wlIdx % 2 === 0 ? 'bg-sky-50' : 'bg-sky-100/60'
                            )}>
                              <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                                <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1 py-0 h-4 sm:h-5 shrink-0 font-semibold bg-sky-200 text-sky-900 border-sky-400" title="Wachleitung">
                                  WL
                                </Badge>
                                {ma.dienstgrad && (
                                  <Badge variant="outline" className="hidden sm:flex text-[10px] px-1.5 py-0.5 h-5 shrink-0 font-semibold min-w-[45px] justify-center">
                                    {ma.dienstgrad}
                                  </Badge>
                                )}
                                {ma.qualifikation && (
                                  <Badge variant="secondary" className="hidden sm:flex text-[10px] px-1.5 py-0.5 h-5 shrink-0 bg-emerald-100 text-emerald-800 min-w-[55px] justify-center">
                                    {QUALIFIKATION_KURZ[ma.qualifikation] || ma.qualifikation}
                                  </Badge>
                                )}
                                <span className="font-medium text-xs sm:text-sm truncate min-w-0 flex-1 text-sky-900">
                                  <span className="hidden sm:inline">{ma.nachname}, {ma.vorname}</span>
                                  <span className="sm:hidden">{ma.nachname}</span>
                                </span>
                              </div>
                            </div>
                            {matrixData.diensttage.map((dt, colIdx) => {
                              const key = `${dt.datum}_${dt.dienst_art}`;
                              const einteilung = matrixData.einteilungen?.[ma.id]?.[key];
                              return (
                                <div key={colIdx} className="w-10 sm:w-12 shrink-0 bg-sky-50/40">
                                  <MatrixCell
                                    mitarbeiter={ma}
                                    diensttag={dt}
                                    einteilung={einteilung}
                                    onCellClick={handleCellClick}
                                    canEdit={canEdit}
                                    fahrzeugTypNummern={matrixData.fahrzeug_typ_nummern}
                                    fahrzeuge={fahrzeuge}
                                    onCellCopy={handleCellCopy}
                                    wunsch={getWunsch(ma.id, dt.datum, dt.dienst_art)}
                                    onWunschOpen={handleWunschOpen}
                                    canSeeWunschHover={true}
                                    currentUserId={currentUser?.id}
                                    isSelected={isCellSelected(ma.id, dt.datum, dt.dienst_art)}
                                    onMultiSelect={handleMultiSelect}
                                    onDragSelectStart={handleDragSelectStart}
                                    onDragSelectEnter={handleDragSelectEnter}
                                    wasDragSelectingRef={wasDragSelectingRef}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </>
                    );
                  })()}

                  {/* Praktikanten-Zeilen (visuell abgegrenzt, separater Block) */}
                  {matrixData.praktikanten && matrixData.praktikanten.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setPrkExpanded(v => !v)}
                        className="flex w-full border-t-2 border-amber-300 bg-amber-100 hover:bg-amber-200/80 transition-colors"
                        data-testid="toggle-praktikanten"
                      >
                        <div className="w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 bg-amber-100 z-10 font-semibold text-xs sm:text-sm text-amber-900">
                          <div className="flex items-center gap-2">
                            {prkExpanded ? <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-700" />}
                            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-700" />
                            <span>Praktikanten ({matrixData.praktikanten.length})</span>
                          </div>
                        </div>
                        {matrixData.diensttage.map((_, i) => (
                          <div key={i} className="w-10 sm:w-12 shrink-0 border-r border-amber-200" />
                        ))}
                      </button>
                      {prkExpanded && matrixData.praktikanten.map((prk, prkIdx) => (
                        <div key={prk.id} className={cn('flex border-b border-amber-100', prkIdx % 2 === 0 ? 'bg-amber-50' : 'bg-amber-100/60')}>
                          <div className={cn(
                            'w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 z-10',
                            prkIdx % 2 === 0 ? 'bg-amber-50' : 'bg-amber-100/60'
                          )}>
                            <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                              <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1 py-0 h-4 sm:h-5 shrink-0 font-semibold bg-amber-100 text-amber-800 border-amber-400" title="Praktikant">
                                P
                              </Badge>
                              {prk.typ && (
                                <Badge variant="secondary" className="hidden sm:flex text-[9px] px-1.5 py-0.5 h-5 shrink-0 bg-amber-50 text-amber-700 border border-amber-200" title={prk.typ}>
                                  {prk.typ === 'RS_PRAKTIKANT' ? 'RS' : prk.typ === 'TRUPP_MANN' ? 'Trupp' : prk.typ === 'SCHUELER' ? 'Schüler' : 'Sonst.'}
                                </Badge>
                              )}
                              <span className="font-medium text-xs sm:text-sm truncate min-w-0 flex-1 text-amber-900">
                                <span className="hidden sm:inline">{prk.nachname}, {prk.vorname}</span>
                                <span className="sm:hidden">{prk.nachname}</span>
                              </span>
                            </div>
                          </div>
                          {matrixData.diensttage.map((dt, colIdx) => {
                            const dtDatum = dt.datum;
                            const imZeitraum = prk.zeitraum_von <= dtDatum && prk.zeitraum_bis >= dtDatum;
                            const key = `${dt.datum}_${dt.dienst_art}`;
                            const einteilung = matrixData.einteilungen?.[prk.id]?.[key];

                            // Außerhalb Zeitraum: nicht klickbar
                            if (!imZeitraum) {
                              return (
                                <div
                                  key={colIdx}
                                  className="w-10 sm:w-12 shrink-0 min-h-[66px] border-r border-amber-100 bg-slate-100/50 flex items-center justify-center text-[9px] text-slate-300"
                                  title="Außerhalb Praktikumszeitraum"
                                />
                              );
                            }

                            // Im Zeitraum: klickbare Zelle (MatrixCell mit Praktikant-Marker)
                            const praktikantAsMitarbeiter = {
                              ...prk,
                              ist_praktikant: true,
                            };
                            return (
                              <div key={colIdx} className="w-10 sm:w-12 shrink-0 bg-amber-50/40">
                                <MatrixCell
                                  mitarbeiter={praktikantAsMitarbeiter}
                                  diensttag={dt}
                                  einteilung={einteilung}
                                  onCellClick={handleCellClick}
                                  canEdit={canEdit}
                                  fahrzeugTypNummern={matrixData.fahrzeug_typ_nummern}
                                  fahrzeuge={fahrzeuge}
                                  onCellCopy={handleCellCopy}
                                  isSelected={isCellSelected(praktikantAsMitarbeiter.id, dt.datum, dt.dienst_art)}
                                  onMultiSelect={handleMultiSelect}
                                  onDragSelectStart={handleDragSelectStart}
                                  onDragSelectEnter={handleDragSelectEnter}
                                  wasDragSelectingRef={wasDragSelectingRef}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </>
                  )}

                  {/* Gäste-Block: echte Gast-Mitarbeiter + 3 anonyme Gast-Slots */}
                  {(() => {
                    const gaeste = matrixData.gaeste || [];
                    const gastSlots = matrixData.gast_slots || [];
                    const hasAnyGast = gaeste.length > 0 || gastSlots.length > 0;
                    if (!hasAnyGast) return null;
                    const renderGastRow = (entry, idx, isSlot) => (
                      <div
                        key={entry.id}
                        className={cn(
                          'flex border-b border-violet-200',
                          idx % 2 === 0 ? 'bg-violet-50' : 'bg-violet-100/60'
                        )}
                      >
                        <div className={cn(
                          'w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 z-10',
                          idx % 2 === 0 ? 'bg-violet-50' : 'bg-violet-100/60'
                        )}>
                          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                            <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1 py-0 h-4 sm:h-5 shrink-0 font-semibold bg-violet-200 text-violet-900 border-violet-400" title="Gast">
                              G
                            </Badge>
                            {!isSlot && entry.dienstgrad && (
                              <Badge variant="outline" className="hidden sm:flex text-[10px] px-1.5 py-0.5 h-5 shrink-0 font-semibold min-w-[45px] justify-center">
                                {entry.dienstgrad}
                              </Badge>
                            )}
                            {!isSlot && entry.qualifikation && (
                              <Badge variant="secondary" className="hidden sm:flex text-[10px] px-1.5 py-0.5 h-5 shrink-0 bg-emerald-100 text-emerald-800 min-w-[55px] justify-center">
                                {QUALIFIKATION_KURZ[entry.qualifikation] || entry.qualifikation}
                              </Badge>
                            )}
                            <span className={cn(
                              'font-medium text-xs sm:text-sm truncate min-w-0 flex-1 text-violet-900',
                              isSlot && 'italic'
                            )}>
                              {isSlot ? (
                                `Gast ${entry.nachname}`
                              ) : (
                                <>
                                  <span className="hidden sm:inline">{entry.nachname}, {entry.vorname}</span>
                                  <span className="sm:hidden">{entry.nachname}</span>
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                        {matrixData.diensttage.map((dt, colIdx) => {
                          const key = `${dt.datum}_${dt.dienst_art}`;
                          const einteilung = matrixData.einteilungen?.[entry.id]?.[key];
                          return (
                            <div key={colIdx} className="w-10 sm:w-12 shrink-0">
                              <MatrixCell
                                mitarbeiter={entry}
                                diensttag={dt}
                                einteilung={einteilung}
                                onCellClick={handleCellClick}
                                canEdit={canEdit}
                                fahrzeugTypNummern={matrixData.fahrzeug_typ_nummern}
                                fahrzeuge={fahrzeuge}
                                onCellCopy={handleCellCopy}
                                isSelected={isCellSelected(entry.id, dt.datum, dt.dienst_art)}
                                onMultiSelect={handleMultiSelect}
                                onDragSelectStart={handleDragSelectStart}
                                onDragSelectEnter={handleDragSelectEnter}
                                wasDragSelectingRef={wasDragSelectingRef}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setGastExpanded(v => !v)}
                          className="flex w-full border-t-2 border-violet-300 bg-violet-100 hover:bg-violet-200/80 transition-colors"
                          data-testid="toggle-gaeste"
                        >
                          <div className="w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 bg-violet-100 z-10 font-semibold text-xs sm:text-sm text-violet-900">
                            <div className="flex items-center gap-2">
                              {gastExpanded ? <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-violet-700" /> : <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-violet-700" />}
                              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-violet-700" />
                              <span>Gäste ({gaeste.length + gastSlots.length})</span>
                            </div>
                          </div>
                          {matrixData.diensttage.map((_, i) => (
                            <div key={i} className="w-10 sm:w-12 shrink-0 border-r border-violet-200" />
                          ))}
                        </button>
                        {gastExpanded && gaeste.map((g, idx) => renderGastRow(g, idx, false))}
                        {gastExpanded && gastSlots.map((gs, idx) => renderGastRow(gs, idx + gaeste.length, true))}
                      </>
                    );
                  })()}

                  {/* Soll-Ist-Footer */}
                  {matrixData.soll_ist && Object.keys(matrixData.soll_ist).length > 0 && (
                    <div className="flex border-t-2 border-primary/30 bg-slate-100">
                      <div className="w-40 sm:w-72 lg:w-80 shrink-0 p-1.5 sm:p-2 border-r-2 sticky left-0 bg-slate-100 z-10 font-medium text-xs sm:text-sm">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="truncate">Besetzung (Ist/Soll)</span>
                        </div>
                      </div>
                      {matrixData.diensttage.map((dt, i) => (
                        <div key={i} className="w-10 sm:w-12 shrink-0 border-r">
                          <SollIstCounter diensttag={dt} sollIst={matrixData.soll_ist} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legende */}
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="font-medium text-muted-foreground">Legende:</span>
              <div className="flex items-center gap-1">
                <Sun className="w-4 h-4 text-amber-600" /><span>Tagdienst</span>
              </div>
              <div className="flex items-center gap-1">
                <Moon className="w-4 h-4 text-indigo-600" /><span>Nachtdienst</span>
              </div>
              <div className="border-l pl-4 flex flex-wrap gap-2">
                {Object.entries(STATUS_CODES).map(([code, info]) => (
                  <div key={code} className="flex items-center gap-1">
                    <div className={cn('w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center', info.bgLight, info.textColor)}>
                      {info.displayCode || code}
                    </div>
                    <span className="text-muted-foreground text-xs">{info.label}</span>
                  </div>
                ))}
              </div>
              {/* Fahrzeug-Farben dynamisch (LHF blau, RTW rot, …) */}
              {fahrzeuge.length > 0 && (
                <div className="border-l pl-4 flex flex-wrap gap-2">
                  {fahrzeuge
                    .filter(fz => !fz.ist_springer && fz.dienstzeit_modell !== 'SPRINGER')
                    .slice(0, 8)
                    .map(fz => {
                      const bg = getFahrzeugFarbe(fz);
                      return (
                        <div key={fz.id} className="flex items-center gap-1">
                          <div
                            className="w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center"
                            style={{ backgroundColor: bg, color: getKontrastFarbe(bg) }}
                            title={fz.funkrufname}
                          >
                            {(fz.fahrzeugtyp_code || fz.typ || '').split(/[-\s]/)[0].slice(0, 3) || '?'}
                          </div>
                          <span className="text-muted-foreground text-xs">{fz.funkrufname}</span>
                        </div>
                      );
                    })}
                </div>
              )}
              <div className="border-l pl-4 flex flex-wrap gap-2">
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-5 bg-sky-100 text-sky-800 border-sky-400">WL</Badge>
                  <span className="text-muted-foreground text-xs">Wachleitung</span>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-5 bg-violet-50 text-violet-700 border-violet-300">ÜG</Badge>
                  <span className="text-muted-foreground text-xs">Übergreifend</span>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-5 bg-amber-100 text-amber-800 border-amber-400">P</Badge>
                  <span className="text-muted-foreground text-xs">Praktikant</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-6 h-6 rounded text-[9px] font-bold flex items-center justify-center bg-amber-200 border border-amber-500 border-dashed text-amber-900">+FZ</div>
                  <span className="text-muted-foreground text-xs">Nur Mitfahrer</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <AssignmentDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          mitarbeiter={selectedCell.mitarbeiter}
          diensttag={selectedCell.diensttag}
          existingData={selectedCell.data}
          fahrzeuge={fahrzeuge}
          fahrzeugtypen={fahrzeugtypen}
          besetztePositionen={besetztePositionen}
          fahrzeugTypNummern={matrixData?.fahrzeug_typ_nummern}
          onAssign={handleAssign}
          alleMitarbeiter={alleUsers}
          aktuelleAbteilungId={abteilungId}
        />
        <AuditLogModal
          open={auditOpen}
          onOpenChange={setAuditOpen}
          abteilungId={abteilungId}
        />
        <DienstwunschModal
          open={wunschModal.open}
          onClose={() => setWunschModal({ open: false, mitarbeiter: null, diensttag: null, wunsch: null })}
          mitarbeiter={wunschModal.mitarbeiter}
          diensttag={wunschModal.diensttag}
          abteilungId={abteilungId}
          fahrzeuge={fahrzeuge}
          existingWunsch={wunschModal.wunsch}
          isOwner={currentUser?.id === wunschModal.mitarbeiter?.id}
          canEditOthersWunsch={canEdit && currentUser?.id !== wunschModal.mitarbeiter?.id}
          canAdminDelete={canEdit}
          onSaved={reloadWuensche}
        />

        {/* Bulk-Assign: schwebende Aktionsleiste + Dialog */}
        {selectedCells.length > 0 && (
          <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900 text-white shadow-lg border border-slate-700"
            data-testid="bulk-action-bar"
          >
            <Badge className="bg-sky-500 text-white shrink-0">
              {selectedCells.length} {selectedCells.length === 1 ? 'Zelle' : 'Zellen'}
            </Badge>
            <span className="text-sm text-slate-200 hidden sm:inline">
              {bulkMitarbeiter?.nachname}, {bulkMitarbeiter?.vorname}
            </span>
            <Button
              size="sm"
              className="bg-sky-500 hover:bg-sky-600 text-white h-8"
              onClick={() => setBulkDialogOpen(true)}
              data-testid="bulk-open-dialog-btn"
            >
              Zuteilen
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-200 hover:text-white hover:bg-slate-800 h-8"
              onClick={clearMultiSelection}
              data-testid="bulk-clear-selection-btn"
            >
              Abwählen
            </Button>
          </div>
        )}

        <BulkAssignmentDialog
          open={bulkDialogOpen}
          onOpenChange={setBulkDialogOpen}
          selectedCells={selectedCells}
          mitarbeiter={bulkMitarbeiter}
          fahrzeuge={fahrzeuge}
          abteilungId={abteilungId}
          onDone={() => {
            clearMultiSelection();
            // Kompletter Reload — Bulk kann viele Statuswechsel betreffen, Optimistic
            // Update wäre pro Zelle zu aufwändig.
            backgroundReloadSollIst();
          }}
        />
      </div>
    </TooltipProvider>
  );
}

export default MatrixView;
